import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PhoneAnimation from '../../components/feature/PhoneAnimation';

const HomePage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          
          {/* Animated background elements */}
          <div className="absolute top-20 left-10 opacity-20">
            <PhoneAnimation variant="floating" size="sm" />
          </div>
          <div className="absolute top-40 right-20 opacity-20">
            <PhoneAnimation variant="pulse" size="sm" />
          </div>
          <div className="absolute bottom-20 left-1/4 opacity-20">
            <PhoneAnimation variant="ringing" size="sm" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:py-40">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <p className="text-teal font-semibold text-lg mb-4">Making a point for your calls</p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                  Stop losing revenue to missed calls.
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-10">
                  Your AI phone agent answers, qualifies, and books 24/7. Built for high-call-volume service businesses (law, real estate, roofing, recruiting). We deploy a custom phone agent that handles new inquiries end-to-end.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8">
                  <a 
                    href="https://tidycal.com/mattv/15-minute-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-10 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
                  >
                    Get Started Free
                  </a>
                  <a 
                    href="tel:+16787376473" 
                    className="px-10 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
                  >
                    Call Live Demo
                  </a>
                </div>
                <div className="flex items-center justify-center lg:justify-start gap-2 text-white/80 text-sm">
                  <span className="font-medium">No guessing policy.</span>
                  <span>If the agent isn't confident, it hands off.</span>
                </div>
              </div>
              
              {/* Hero Phone Animation */}
              <div className="hidden lg:flex items-center justify-center">
                <PhoneAnimation variant="hero" size="xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                The Cost of Missed Calls in 2025
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Every missed call is a lost opportunity. Here's what businesses are losing right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-3xl text-teal"></i>
                </div>
                <div className="text-5xl font-bold text-navy mb-2">62%</div>
                <p className="text-gray-600 font-medium mb-2">of calls go unanswered</p>
                <p className="text-sm text-gray-500">Average across service industries during business hours</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-money-dollar-circle-line text-3xl text-teal"></i>
                </div>
                <div className="text-5xl font-bold text-navy mb-2">$1,200</div>
                <p className="text-gray-600 font-medium mb-2">average value per lead</p>
                <p className="text-sm text-gray-500">For law firms, real estate, and home services</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-time-line text-3xl text-teal"></i>
                </div>
                <div className="text-5xl font-bold text-navy mb-2">85%</div>
                <p className="text-gray-600 font-medium mb-2">won't call back</p>
                <p className="text-sm text-gray-500">Callers move to your competitor within 5 minutes</p>
              </div>
            </div>
            <div className="mt-12 bg-gradient-to-r from-teal/10 to-teal/5 rounded-2xl p-8 text-center">
              <p className="text-2xl font-bold text-navy mb-2">
                That's <span className="text-teal">$74,400</span> in lost revenue per month
              </p>
              <p className="text-gray-600">
                For a business receiving 100 calls/day with 62% missed call rate
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                How PointCalls Stops the Leak
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our AI phone agent captures every opportunity, 24/7
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 bg-teal/20 rounded-2xl animate-ping opacity-0 group-hover:opacity-100 transition-opacity" style={{ animationDuration: '2s' }}></div>
                  <div className="relative w-20 h-20 bg-teal-light rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-customer-service-2-line text-4xl text-teal"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Answer every call in under 2 rings</h3>
                <p className="text-gray-600">
                  Even nights and weekends. No caller waits, no opportunity lost.
                </p>
              </div>
              <div className="text-center group">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 bg-teal/20 rounded-2xl animate-ping opacity-0 group-hover:opacity-100 transition-opacity" style={{ animationDuration: '2s' }}></div>
                  <div className="relative w-20 h-20 bg-teal-light rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-user-search-line text-4xl text-teal"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Qualify before human handoff</h3>
                <p className="text-gray-600">
                  Route serious callers to your team; filter tire-kickers and spam.
                </p>
              </div>
              <div className="text-center group">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 bg-teal/20 rounded-2xl animate-ping opacity-0 group-hover:opacity-100 transition-opacity" style={{ animationDuration: '2s' }}></div>
                  <div className="relative w-20 h-20 bg-teal-light rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-calendar-check-line text-4xl text-teal"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Book and log automatically</h3>
                <p className="text-gray-600">
                  Schedule appointments and log call notes + transcript into your systems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                How it works
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From discovery to deployment in weeks, not months.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-navy text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  01
                </div>
                <h3 className="text-xl font-semibold text-navy mb-4 text-center">Map your call flows</h3>
                <p className="text-gray-600 text-center">
                  We document your intake questions, routing logic, and handoff rules—customized to your practice.
                </p>
              </div>
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-navy text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  02
                </div>
                <h3 className="text-xl font-semibold text-navy mb-4 text-center">Deploy + integrate</h3>
                <p className="text-gray-600 text-center">
                  Connect to your phones, calendar, and CRM. We handle the technical setup.
                </p>
              </div>
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-navy text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  03
                </div>
                <h3 className="text-xl font-semibold text-navy mb-4 text-center">Monitor + optimize</h3>
                <p className="text-gray-600 text-center">
                  Review transcripts weekly and refine the agent based on real call data.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Preview */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Built for your industry
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Tailored call flows and guardrails for high-call-volume service businesses.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/industries#law" className="group bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-teal hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-scales-3-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">Law Firms</h3>
                <p className="text-sm text-gray-600">Case intake & consultation scheduling</p>
              </Link>
              <Link to="/industries#real-estate" className="group bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-teal hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-home-4-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">Real Estate</h3>
                <p className="text-sm text-gray-600">Property showings & buyer qualification</p>
              </Link>
              <Link to="/industries#roofing" className="group bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-teal hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-building-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">Roofing</h3>
                <p className="text-sm text-gray-600">Estimate requests & inspection booking</p>
              </Link>
              <Link to="/industries#recruiting" className="group bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-teal hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-team-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-2">Recruiting</h3>
                <p className="text-sm text-gray-600">Candidate screening & interviews</p>
              </Link>
            </div>
            <div className="text-center mt-12">
              <Link 
                to="/industries" 
                className="inline-block px-8 py-4 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                View All Industries
              </Link>
            </div>
          </div>
        </section>

        {/* Blog Preview */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Latest from Our Blog
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Insights on AI phone technology, lead capture, and business growth.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <Link to="/blog/missed-calls-revenue-killer-2025" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/7093ca5a2cd8c95ae7bdb01a9065bf17.jpg" 
                    alt="Missed Calls Revenue" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-teal uppercase tracking-wide">2025 Trends</span>
                  <h3 className="text-lg font-semibold text-navy mt-2 mb-3 group-hover:text-teal transition-colors">Missed Calls: The Silent Revenue Killer in 2025</h3>
                  <p className="text-sm text-gray-600">Why 62% of business calls go unanswered and the $74,400 monthly cost.</p>
                </div>
              </Link>
              <Link to="/blog/after-hours-calls-2025-data" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Night%20time%20city%20office%20building%20with%20lights%20on%20showing%20late%20night%20business%20operations%20and%2024%20hour%20service%20concept%20with%20modern%20architecture&width=600&height=400&seq=blog7&orientation=landscape" 
                    alt="After Hours Calls" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-teal uppercase tracking-wide">Research</span>
                  <h3 className="text-lg font-semibold text-navy mt-2 mb-3 group-hover:text-teal transition-colors">The 2025 After-Hours Call Report: What the Data Shows</h3>
                  <p className="text-sm text-gray-600">73% of high-value leads call outside business hours. Here's why.</p>
                </div>
              </Link>
              <Link to="/blog/ai-vs-voicemail-2025" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Futuristic%20AI%20assistant%20concept%20with%20voice%20waves%20and%20neural%20network%20visualization%20representing%20intelligent%20phone%20conversation%20technology%20in%20teal%20and%20white%20colors&width=600&height=400&seq=blog2&orientation=landscape" 
                    alt="AI vs Voicemail" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-teal uppercase tracking-wide">Comparison</span>
                  <h3 className="text-lg font-semibold text-navy mt-2 mb-3 group-hover:text-teal transition-colors">AI Phone Agents vs. Voicemail: 2025 Conversion Rates</h3>
                  <p className="text-sm text-gray-600">Why 85% of callers won't leave a voicemail—and what converts instead.</p>
                </div>
              </Link>
            </div>
            <div className="text-center mt-12">
              <Link 
                to="/blog" 
                className="inline-block px-8 py-4 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                View All Articles
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white relative overflow-hidden">
          {/* Animated phone icons */}
          <div className="absolute top-10 left-10 opacity-10">
            <PhoneAnimation variant="floating" size="md" />
          </div>
          <div className="absolute bottom-10 right-10 opacity-10">
            <PhoneAnimation variant="pulse" size="md" />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to stop missed calls from leaking revenue?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Join hundreds of service businesses capturing every opportunity with AI phone agents.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="https://tidycal.com/mattv/15-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-teal text-white text-lg font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Book a Demo
              </a>
              <a 
                href="tel:+16787376473" 
                className="px-10 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
